import React from 'react';
import './MobileTermsPage.css';

// Full Terms of Service rendered in mobile aesthetic
export default function MobileTermsPage(){
  const scrollToTop = () => window.scrollTo({ top:0, behavior:'smooth' });
  return (
    <div className="mobile-terms-page">
      <header className="mtp-header" role="banner">
        <a href="/m" className="mtp-home-btn" aria-label="Go home">
          <svg className="mtp-home-ic" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
            <defs>
              <linearGradient id="mtpHomeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#6b7280"/>
                <stop offset="55%" stopColor="#9ca3af"/>
                <stop offset="100%" stopColor="#d1d5db"/>
              </linearGradient>
            </defs>
            <path d="M4 11.5 12 5l8 6.5v6.2c0 .9-.7 1.6-1.6 1.6H5.6c-.9 0-1.6-.7-1.6-1.6v-6.2Z" fill="url(#mtpHomeGrad)" stroke="rgba(15,32,58,0.15)" strokeWidth="1" strokeLinejoin="round"/>
            <path d="M9.75 19.3v-4.1c0-.7.55-1.2 1.25-1.2h2c.7 0 1.25.5 1.25 1.2v4.1" fill="none" stroke="#fff" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" opacity="0.85"/>
          </svg>
        </a>
        <h1 className="mtp-title">Terms of Service</h1>
        <p className="mtp-sub">These terms govern your use of DocuHounds. By using our platform, you agree to these terms.</p>
        <div className="mtp-accent" />
      </header>

      <nav className="mtp-toc" aria-label="Table of contents">
        <ul>
          <li><a href="#introduction">1. Introduction</a></li>
          <li><a href="#eligibility">2. Eligibility</a></li>
          <li><a href="#responsibilities">3. User Responsibilities</a></li>
          <li><a href="#acceptable-use">4. Acceptable Use</a></li>
          <li><a href="#ai-usage">5. AI Usage Notice</a></li>
          <li><a href="#intellectual-property">6. Intellectual Property</a></li>
          <li><a href="#subscriptions">7. Subscriptions & Payments</a></li>
          <li><a href="#termination">8. Account Suspension or Termination</a></li>
          <li><a href="#disclaimers">9. Disclaimers & Limitation of Liability</a></li>
          <li><a href="#changes">10. Changes to Terms</a></li>
          <li><a href="#governing-law">11. Governing Law</a></li>
          <li><a href="#contact">12. Contact Information</a></li>
        </ul>
      </nav>

      {/* Sections */}
      <section id="introduction" className="mtp-section">
        <h2 className="mtp-section-title">1. Introduction</h2>
        <p className="mtp-text">Welcome to DocuHounds, an AI-powered academic assistant platform designed to help students organize their coursework, manage assignments, and enhance their learning experience. DocuHounds is operated by DocuHounds Inc. ("we," "us," or "our").</p>
        <p className="mtp-text">By accessing or using the DocuHounds platform (including our website, mobile applications, and related services), you ("user," "you," or "your") agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our platform.</p>
        <div className="mtp-subcard mtp-warn"><p className="mtp-text"><strong>Important:</strong> These Terms form a legally binding agreement between you and DocuHounds. Please read them carefully.</p></div>
      </section>

      <section id="eligibility" className="mtp-section">
        <h2 className="mtp-section-title">2. Eligibility</h2>
        <h3 className="mtp-subheading">Age Requirements</h3>
        <ul className="mtp-list">
          <li>You must be at least 13 years old to use DocuHounds</li>
          <li>If you are under 18, you must have permission from a parent, guardian, or educational institution</li>
          <li>Users under 13 may only access DocuHounds through a supervised institutional account</li>
        </ul>
        <h3 className="mtp-subheading">Account Information</h3>
        <ul className="mtp-list">
          <li>Provide accurate, current, and complete registration information</li>
          <li>Update account info when it changes</li>
          <li>False information may lead to suspension or termination</li>
        </ul>
        <h3 className="mtp-subheading">Educational Use</h3>
        <p className="mtp-text">DocuHounds is designed for students, educators, and educational institutions. While we welcome all learners, our platform is tailored for academic environments and educational purposes.</p>
      </section>

      <section id="responsibilities" className="mtp-section">
        <h2 className="mtp-section-title">3. User Responsibilities</h2>
        <h3 className="mtp-subheading">Account Security</h3>
        <ul className="mtp-list">
          <li>Maintain confidentiality of login credentials</li>
          <li>You are responsible for all activity under your account</li>
          <li>Notify us immediately of suspected unauthorized access</li>
          <li>Use strong unique passwords (enable 2FA when available)</li>
        </ul>
        <h3 className="mtp-subheading">Prohibited Activities</h3>
        <p className="mtp-text">You agree not to:</p>
        <div className="mtp-grid mtp-prohibited">
          <div className="mtp-badge-item"><span className="mtp-ic">❌</span><strong>Academic Dishonesty:</strong> Cheating, plagiarism, or misconduct</div>
          <div className="mtp-badge-item"><span className="mtp-ic">❌</span><strong>Harmful Content:</strong> Abusive, harassing, defamatory, or illegal material</div>
          <div className="mtp-badge-item"><span className="mtp-ic">❌</span><strong>Platform Abuse:</strong> Disrupting, damaging, reverse-engineering services</div>
          <div className="mtp-badge-item"><span className="mtp-ic">❌</span><strong>Unauthorized Access:</strong> Accessing accounts/data without permission</div>
        </div>
      </section>

      <section id="acceptable-use" className="mtp-section">
        <h2 className="mtp-section-title">4. Acceptable Use</h2>
        <h3 className="mtp-subheading">Intended Purpose</h3>
        <p className="mtp-text">DocuHounds is intended solely for educational and personal academic purposes. It helps students organize coursework, manage assignments, and enhance learning through AI-powered features.</p>
        <h3 className="mtp-subheading">Commercial Restrictions</h3>
        <ul className="mtp-list">
          <li>No commercial resale or redistribution of content/services</li>
          <li>No automated scraping, crawling, or data mining</li>
          <li>Business use outside educational contexts requires explicit permission</li>
        </ul>
        <h3 className="mtp-subheading">Identity and Representation</h3>
        <ul className="mtp-list">
          <li>No impersonation of individuals or entities</li>
          <li>No misrepresentation of educational affiliation</li>
          <li>Be truthful about identity and academic status</li>
        </ul>
        <div className="mtp-two-col">
          <div className="mtp-subcard mtp-good">
            <h4 className="mtp-mini">✅ Acceptable Uses</h4>
            <ul className="mtp-list">
              <li>Organizing personal coursework and assignments</li>
              <li>Using AI chat to understand materials</li>
              <li>Syncing academic calendar items</li>
              <li>Collaborating on group projects</li>
            </ul>
          </div>
          <div className="mtp-subcard mtp-bad">
            <h4 className="mtp-mini">❌ Unacceptable Uses</h4>
            <ul className="mtp-list">
              <li>Sharing answers or promoting cheating</li>
              <li>Uploading copyrighted materials without rights</li>
              <li>Creating multiple fake accounts</li>
              <li>Selling access to platform features</li>
            </ul>
          </div>
        </div>
      </section>

      <section id="ai-usage" className="mtp-section">
        <h2 className="mtp-section-title">5. AI Usage Notice</h2>
        <div className="mtp-subcard mtp-info">
          <h3 className="mtp-subheading">🤖 How We Use AI</h3>
          <p className="mtp-text">DocuHounds uses artificial intelligence to enhance your learning experience (study assistance, calendar synchronization, document analysis, intelligent chat).</p>
        </div>
        <h3 className="mtp-subheading">AI-Powered Features</h3>
        <ul className="mtp-list">
          <li><strong>Document Chat:</strong> Ask questions about uploaded materials</li>
          <li><strong>Calendar Sync:</strong> Extract dates and deadlines</li>
          <li><strong>Study Assistance:</strong> Generate guides and summaries</li>
          <li><strong>Task Organization:</strong> Categorize assignments intelligently</li>
        </ul>
        <h3 className="mtp-subheading">Important Disclaimers</h3>
        <div className="mtp-grid mtp-disclaimers">
          <div className="mtp-badge-item"><span className="mtp-ic">⚠️</span><strong>Review Output:</strong> Verify AI content before academic use</div>
          <div className="mtp-badge-item"><span className="mtp-ic">🎯</span><strong>Suggestions Only:</strong> AI aids, not authoritative answers</div>
          <div className="mtp-badge-item"><span className="mtp-ic">🔍</span><strong>Accuracy Not Guaranteed:</strong> Content may contain errors</div>
          <div className="mtp-badge-item"><span className="mtp-ic">📚</span><strong>Supplement:</strong> Support learning—don’t replace it</div>
        </div>
      </section>

      <section id="intellectual-property" className="mtp-section">
        <h2 className="mtp-section-title">6. Intellectual Property</h2>
        <h3 className="mtp-subheading">DocuHounds Platform</h3>
        <p className="mtp-text">All aspects of the platform (code, design, branding, logos, features) are owned by DocuHounds Inc. and protected by intellectual property laws.</p>
        <h3 className="mtp-subheading">Your Content</h3>
        <div className="mtp-two-col">
          <div className="mtp-subcard">
            <h4 className="mtp-mini">You Own Your Content</h4>
            <p className="mtp-text">You retain ownership of documents, notes, and other content you upload.</p>
          </div>
          <div className="mtp-subcard">
            <h4 className="mtp-mini">Limited License</h4>
            <ul className="mtp-list">
              <li>Process documents for functionality (chat, calendar)</li>
              <li>Store/back up to provide services</li>
              <li>Analyze patterns to improve AI (anonymized)</li>
            </ul>
          </div>
        </div>
        <h3 className="mtp-subheading">What We Don't Do</h3>
        <div className="mtp-grid mtp-protect">
          <div className="mtp-badge-item"><span className="mtp-ic">🚫</span><strong>No Public Sharing:</strong> Private content stays private</div>
          <div className="mtp-badge-item"><span className="mtp-ic">🚫</span><strong>No Resale:</strong> We never sell your documents</div>
          <div className="mtp-badge-item"><span className="mtp-ic">🚫</span><strong>No Unauthorized Use:</strong> Content used only to deliver services</div>
        </div>
      </section>

      <section id="subscriptions" className="mtp-section">
        <h2 className="mtp-section-title">7. Subscriptions & Payments</h2>
        <div className="mtp-subcard mtp-success">
          <h3 className="mtp-subheading">🎁 Current Beta Status</h3>
          <p className="mtp-text">DocuHounds is in beta and free to use. Early beta users will receive a lifetime 50% discount on all future subscription plans.</p>
        </div>
        <h3 className="mtp-subheading">Future Paid Plans</h3>
        <p className="mtp-text">When subscription tiers launch, the following apply:</p>
        <h4 className="mtp-mini">Billing & Renewals</h4>
        <ul className="mtp-list">
          <li>Auto-renew unless cancelled before renewal date</li>
          <li>Email notice before automatic renewals</li>
          <li>Price changes with 30 days advance notice</li>
          <li>Payments via secure processors (Stripe/PayPal)</li>
        </ul>
        <h4 className="mtp-mini">Cancellations & Refunds</h4>
        <ul className="mtp-list">
          <li>Cancel anytime in account settings (end of billing period)</li>
          <li>No partial month refunds except as required by law</li>
          <li>Full refund if unsatisfied in first 7 days</li>
        </ul>
        <h4 className="mtp-mini">Beta User Benefits</h4>
        <div className="mtp-grid mtp-benefits">
          <div className="mtp-badge-item"><span className="mtp-ic">🏆</span><strong>Lifetime 50% Discount:</strong> Permanent savings on paid plans</div>
          <div className="mtp-badge-item"><span className="mtp-ic">⚡</span><strong>Priority Features:</strong> Early access to new capabilities</div>
          <div className="mtp-badge-item"><span className="mtp-ic">🎯</span><strong>Grandfathered Access:</strong> Current free features remain free</div>
        </div>
      </section>

      <section id="termination" className="mtp-section">
        <h2 className="mtp-section-title">8. Account Suspension or Termination</h2>
        <h3 className="mtp-subheading">Termination by DocuHounds</h3>
        <p className="mtp-text">We may suspend or terminate accounts for:</p>
        <ul className="mtp-list">
          <li>Terms violations</li>
          <li>Harmful activities toward other users/platform</li>
          <li>Non-payment (when subscriptions launch)</li>
          <li>False registration information</li>
          <li>Illegal use of services</li>
        </ul>
        <h3 className="mtp-subheading">Termination Process</h3>
        <div className="mtp-grid mtp-steps">
          <div className="mtp-badge-item"><span className="mtp-step">1</span><strong>Warning:</strong> Minor violations usually receive warning</div>
          <div className="mtp-badge-item"><span className="mtp-step">2</span><strong>Suspension:</strong> Temporary suspension for repeated/serious issues</div>
          <div className="mtp-badge-item"><span className="mtp-step">3</span><strong>Termination:</strong> Permanent closure for severe violations</div>
        </div>
        <h3 className="mtp-subheading">Termination by You</h3>
        <ul className="mtp-list">
          <li>Cancel anytime via account settings</li>
          <li>Deletion is permanent and cannot be undone</li>
          <li>Export option available before deletion</li>
          <li>Some info retained as required by law</li>
        </ul>
        <h3 className="mtp-subheading">Effect of Termination</h3>
        <p className="mtp-text">Upon termination:</p>
        <ul className="mtp-list">
          <li>Access revoked immediately</li>
          <li>Uploaded content deleted per retention policy</li>
          <li>Outstanding payment obligations remain</li>
          <li>Surviving provisions continue to apply</li>
        </ul>
      </section>

      <section id="disclaimers" className="mtp-section">
        <h2 className="mtp-section-title">9. Disclaimers & Limitation of Liability</h2>
        <div className="mtp-subcard mtp-warn">
          <h3 className="mtp-subheading">⚠️ Important Legal Disclaimers</h3>
          <p className="mtp-text">Please read this section carefully as it limits our liability and affects your legal rights.</p>
        </div>
        <h3 className="mtp-subheading">Service Disclaimers</h3>
        <ul className="mtp-list">
          <li><strong>"As-Is" Service:</strong> Provided without warranties of any kind</li>
          <li><strong>No Guarantee of Accuracy:</strong> AI content may be inaccurate</li>
          <li><strong>Service Availability:</strong> No guarantee of uninterrupted service</li>
          <li><strong>Third-Party Services:</strong> Not responsible for integrations</li>
        </ul>
        <h3 className="mtp-subheading">Academic Responsibility</h3>
        <ul className="mtp-list">
          <li>Missed assignments or deadlines</li>
          <li>Academic outcomes or grades</li>
          <li>Decisions based on AI suggestions</li>
          <li>Integrity violations by users</li>
          <li>Loss of work due to interruptions</li>
        </ul>
        <h3 className="mtp-subheading">Limitation of Liability</h3>
        <p className="mtp-text">Maximum liability is the amount paid in prior 12 months or $100 (whichever greater).</p>
        <h3 className="mtp-subheading">Indemnification</h3>
        <p className="mtp-text">You agree to indemnify and hold us harmless from claims arising from platform use or Terms violations.</p>
      </section>

      <section id="changes" className="mtp-section">
        <h2 className="mtp-section-title">10. Changes to Terms</h2>
        <h3 className="mtp-subheading">How We Update Terms</h3>
        <p className="mtp-text">We may update these Terms to reflect service, legal, or practice changes.</p>
        <h3 className="mtp-subheading">Notification Process</h3>
        <div className="mtp-two-col">
          <div className="mtp-subcard">
            <h4 className="mtp-mini">📧 Major Changes</h4>
            <ul className="mtp-list">
              <li>Email notification</li>
              <li>In-app banner</li>
              <li>30-day notice period</li>
            </ul>
          </div>
          <div className="mtp-subcard">
            <h4 className="mtp-mini">📝 Minor Changes</h4>
            <ul className="mtp-list">
              <li>Updated last modified date</li>
              <li>In-app notice</li>
              <li>Effective immediately on posting</li>
            </ul>
          </div>
        </div>
        <h3 className="mtp-subheading">Your Options</h3>
        <ul className="mtp-list">
          <li>Continued use indicates acceptance</li>
          <li>Disagree? Cancel account</li>
          <li>Export data if leaving</li>
        </ul>
        <div className="mtp-subcard mtp-info">
          <h4 className="mtp-mini">Version History</h4>
          <p className="mtp-text">We maintain a record of major changes. Contact us for previous versions.</p>
        </div>
      </section>

      <section id="governing-law" className="mtp-section">
        <h2 className="mtp-section-title">11. Governing Law</h2>
        <h3 className="mtp-subheading">Applicable Law</h3>
        <p className="mtp-text">Terms governed by laws of the Commonwealth of Massachusetts and the United States, without regard to conflict principles.</p>
        <h3 className="mtp-subheading">Jurisdiction</h3>
        <p className="mtp-text">Disputes resolved in Massachusetts state or federal courts; you consent to jurisdiction.</p>
        <h3 className="mtp-subheading">International Users</h3>
        <p className="mtp-text">Accessing from outside the US means you acknowledge governance by US law.</p>
        <h3 className="mtp-subheading">Dispute Resolution</h3>
        <div className="mtp-grid mtp-steps">
          <div className="mtp-badge-item"><span className="mtp-step">1</span><strong>Direct Contact:</strong> Reach out first for informal resolution</div>
          <div className="mtp-badge-item"><span className="mtp-step">2</span><strong>Mediation:</strong> If informal fails, mediation recommended</div>
          <div className="mtp-badge-item"><span className="mtp-step">3</span><strong>Legal Action:</strong> Last resort in Massachusetts courts</div>
        </div>
      </section>

      <section id="contact" className="mtp-section">
        <h2 className="mtp-section-title">12. Contact Information</h2>
        <h3 className="mtp-subheading">Questions About These Terms?</h3>
        <p className="mtp-text">If you have questions about these Terms or need clarification, we're here to help.</p>
        <div className="mtp-grid mtp-contact">
          <div className="mtp-badge-item"><span className="mtp-ic">📧</span><strong>Legal Questions:</strong> <a href="mailto:legal@docuhounds.com">legal@docuhounds.com</a></div>
          <div className="mtp-badge-item"><span className="mtp-ic">💬</span><strong>General Support:</strong> <a href="mailto:support@docuhounds.com">support@docuhounds.com</a></div>
          <div className="mtp-badge-item"><span className="mtp-ic">⏱️</span><strong>Response Times:</strong> Legal 3–5 days • General 1–2 days • Urgent same day</div>
        </div>
        <div className="mtp-subcard mtp-info"><p className="mtp-text">We value your questions and feedback. Reach out if anything needs clarification.</p></div>
      </section>

      <footer className="mtp-footer">
        <button type="button" className="mtp-top-btn" onClick={scrollToTop}>↑ Back to Top</button>
        <p className="mtp-last-updated">Last updated: January 1, 2025</p>
      </footer>
    </div>
  );
}
